import React, { useState, useEffect } from 'react';
import {
  AIAgent,
  AgentCapability,
  AgentLearningData,
  LearnedPattern,
  AgentInsight
} from '../../../types/aiAgent';
import { tone, toneForMeasure } from "components/ui/tone";
import { clickable } from "components/ui/interactive";
import { SelectField } from "components/ui/select-field";

interface LearningMilestone {
  id: string;
  timestamp: Date;
  type: 'capability_gained' | 'pattern_learned' | 'strategy_refined' | 'behavior_changed' | 'insight_discovered';
  title: string;
  description: string;
  impact: 'low' | 'medium' | 'high';
  relatedCapabilities: string[];
  metrics?: {
    before: number;
    after: number;
    improvement: number;
  };
}

interface AgentLearningProgressionProps {
  agent: AIAgent;
  timeRange?: '7d' | '30d' | '90d' | '1y' | 'all';
  onMilestoneSelect?: (milestone: LearningMilestone) => void;
  onCapabilitySelect?: (capability: AgentCapability) => void;
}

export const AgentLearningProgression: React.FC<AgentLearningProgressionProps> = ({
  agent,
  timeRange = '30d',
  onMilestoneSelect,
  onCapabilitySelect
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'capabilities' | 'patterns' | 'insights' | 'evolution'>('overview');
  const [learningMilestones, setLearningMilestones] = useState<LearningMilestone[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLearningData();
  }, [agent.id, timeRange]);

  const loadLearningData = async () => {
    try {
      setLoading(true);
      // Mock implementation - would load from service
      const mockMilestones = generateMockLearningMilestones();
      setLearningMilestones(mockMilestones);
    } catch (error) {
      console.error('Failed to load learning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockLearningMilestones = (): LearningMilestone[] => [
    {
      id: 'milestone_1',
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      type: 'capability_gained',
      title: 'Advanced Statistical Analysis',
      description: 'Gained proficiency in Bayesian A/B testing methods',
      impact: 'high',
      relatedCapabilities: ['statistical_analysis', 'bayesian_methods'],
      metrics: { before: 0.72, after: 0.89, improvement: 0.17 }
    },
    {
      id: 'milestone_2',
      timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      type: 'pattern_learned',
      title: 'Seasonal Performance Patterns',
      description: 'Identified recurring seasonal patterns in test performance',
      impact: 'medium',
      relatedCapabilities: ['pattern_recognition', 'temporal_analysis'],
      metrics: { before: 0.65, after: 0.78, improvement: 0.13 }
    },
    {
      id: 'milestone_3',
      timestamp: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000),
      type: 'strategy_refined',
      title: 'Test Duration Optimization',
      description: 'Refined strategy for determining optimal test durations',
      impact: 'high',
      relatedCapabilities: ['test_design', 'optimization'],
      metrics: { before: 0.71, after: 0.85, improvement: 0.14 }
    },
    {
      id: 'milestone_4',
      timestamp: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000),
      type: 'behavior_changed',
      title: 'Risk Assessment Enhancement',
      description: 'Improved risk assessment behavior for high-impact decisions',
      impact: 'high',
      relatedCapabilities: ['risk_assessment', 'decision_making'],
      metrics: { before: 0.68, after: 0.82, improvement: 0.14 }
    },
    {
      id: 'milestone_5',
      timestamp: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000),
      type: 'insight_discovered',
      title: 'Cross-Campaign Learning',
      description: 'Discovered insights about cross-campaign performance correlations',
      impact: 'medium',
      relatedCapabilities: ['cross_analysis', 'insight_generation'],
      metrics: { before: 0.63, after: 0.75, improvement: 0.12 }
    }
  ];

  const getMilestoneIcon = (type: LearningMilestone['type']) => {
    const icons = {
      capability_gained: '🎯',
      pattern_learned: '🔍',
      strategy_refined: '⚙️',
      behavior_changed: '🔄',
      insight_discovered: '💡'
    };
    return icons[type];
  };

  const getImpactColor = (impact: 'low' | 'medium' | 'high') => {
    const colors = {
      low: 'text-content-secondary bg-surface-sunken',
      medium: 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
      high: 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
    };
    return colors[impact];
  };

  const getCapabilityProficiencyColor = (proficiency: number) =>
    tone(
      toneForMeasure(proficiency, [
        { atLeast: 90, tone: 'success' },
        { atLeast: 75, tone: 'info' },
        { atLeast: 60, tone: 'warning' },
      ], 'danger')
    ).chip;

  const calculateLearningVelocity = () => {
    const recentMilestones = learningMilestones.filter(m => 
      m.timestamp > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    return recentMilestones.length;
  };

  const calculateAverageImprovement = () => {
    const milestonesWithMetrics = learningMilestones.filter(m => m.metrics);
    if (milestonesWithMetrics.length === 0) return 0;
    
    const totalImprovement = milestonesWithMetrics.reduce((sum, m) => 
      sum + (m.metrics?.improvement || 0), 0
    );
    return totalImprovement / milestonesWithMetrics.length;
  };

  const _getTopCapabilities = () => {
    return agent.capabilities
      .sort((a, b) => b.proficiency - a.proficiency)
      .slice(0, 5);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              Learning Progression - {agent.name}
            </h3>
            <p className="text-sm text-content-secondary mt-1">
              Capability evolution and learning milestones
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
              {calculateLearningVelocity()}
            </div>
            <div className="text-sm text-content-secondary">Learning Events (30d)</div>
          </div>
        </div>

        {/* Learning Summary */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
            <div className="text-sm text-blue-600 dark:text-blue-300">Total Capabilities</div>
            <div className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {agent.capabilities.length}
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
            <div className="text-sm text-green-600 dark:text-green-300">Avg Proficiency</div>
            <div className="text-lg font-semibold text-green-700 dark:text-green-300">
              {Math.round(agent.capabilities.reduce((sum, c) => sum + c.proficiency, 0) / agent.capabilities.length)}%
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
            <div className="text-sm text-purple-600 dark:text-purple-300">Learning Velocity</div>
            <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
              {calculateLearningVelocity()}/month
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3">
            <div className="text-sm text-orange-600 dark:text-orange-300">Avg Improvement</div>
            <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">
              {(calculateAverageImprovement() * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'capabilities', label: 'Capabilities', icon: '🎯' },
            { id: 'patterns', label: 'Learned Patterns', icon: '🔍' },
            { id: 'insights', label: 'Insights', icon: '💡' },
            { id: 'evolution', label: 'Evolution Timeline', icon: '📈' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <LearningOverviewTab
            agent={agent}
            milestones={learningMilestones}
            onMilestoneSelect={onMilestoneSelect}
            getMilestoneIcon={getMilestoneIcon}
            getImpactColor={getImpactColor}
          />
        )}

        {activeTab === 'capabilities' && (
          <CapabilitiesTab
            capabilities={agent.capabilities}
            onCapabilitySelect={onCapabilitySelect}
            getCapabilityProficiencyColor={getCapabilityProficiencyColor}
          />
        )}

        {activeTab === 'patterns' && (
          <LearnedPatternsTab
            learningData={agent.learningData}
          />
        )}

        {activeTab === 'insights' && (
          <InsightsTab
            learningData={agent.learningData}
          />
        )}

        {activeTab === 'evolution' && (
          <EvolutionTimelineTab
            milestones={learningMilestones}
            onMilestoneSelect={onMilestoneSelect}
            getMilestoneIcon={getMilestoneIcon}
            getImpactColor={getImpactColor}
          />
        )}
      </div>
    </div>
  );
};

// Learning Overview Tab
const LearningOverviewTab: React.FC<{
  agent: AIAgent;
  milestones: LearningMilestone[];
  onMilestoneSelect?: (milestone: LearningMilestone) => void;
  getMilestoneIcon: (type: LearningMilestone['type']) => string;
  getImpactColor: (impact: 'low' | 'medium' | 'high') => string;
}> = ({ agent, milestones, onMilestoneSelect, getMilestoneIcon, getImpactColor }) => {
  const recentMilestones = milestones.slice(0, 5);
  const topCapabilities = agent.capabilities
    .sort((a, b) => b.proficiency - a.proficiency)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Recent Learning Milestones */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Recent Learning Milestones</h4>
        <div className="space-y-3">
          {recentMilestones.map(milestone => (
            <div
              key={milestone.id}
              className="border border-line rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
              {...clickable(() => onMilestoneSelect?.(milestone))}
            >
              <div className="flex items-start space-x-4">
                <div className="text-2xl">{getMilestoneIcon(milestone.type)}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-content">{milestone.title}</h5>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(milestone.impact)}`}>
                        {milestone.impact.toUpperCase()}
                      </span>
                      <span className="text-sm text-content-muted">
                        {milestone.timestamp.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{milestone.description}</p>
                  {milestone.metrics && (
                    <div className="text-sm text-content-secondary">
                      Improvement: {(milestone.metrics.improvement * 100).toFixed(1)}% 
                      ({(milestone.metrics.before * 100).toFixed(1)}% → {(milestone.metrics.after * 100).toFixed(1)}%)
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Capabilities */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Top Capabilities</h4>
        <div className="grid grid-cols-2 gap-4">
          {topCapabilities.map(capability => (
            <div key={capability.id} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-content">{capability.name}</h5>
                <span className="text-sm font-medium text-content-secondary">
                  {capability.proficiency}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2 mb-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    capability.proficiency >= 90 ? 'bg-green-500' :
                    capability.proficiency >= 75 ? 'bg-blue-500' :
                    capability.proficiency >= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${capability.proficiency}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-content-muted">
                <span>Used {capability.usageCount} times</span>
                <span>{(capability.successRate * 100).toFixed(1)}% success</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Statistics */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Learning Statistics</h4>
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-content">
                {agent.learningData.knowledgeBase.patterns.length}
              </div>
              <div className="text-sm text-content-secondary">Patterns Learned</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-content">
                {agent.learningData.knowledgeBase.bestPractices.length}
              </div>
              <div className="text-sm text-content-secondary">Best Practices</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-content">
                {agent.learningData.adaptations.behaviorChanges.length}
              </div>
              <div className="text-sm text-content-secondary">Behavior Changes</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Capabilities Tab
const CapabilitiesTab: React.FC<{
  capabilities: AgentCapability[];
  onCapabilitySelect?: (capability: AgentCapability) => void;
  getCapabilityProficiencyColor: (proficiency: number) => string;
}> = ({ capabilities, onCapabilitySelect, getCapabilityProficiencyColor }) => {
  const [sortBy, setSortBy] = useState<'proficiency' | 'usage' | 'success' | 'recent'>('proficiency');
  
  const sortedCapabilities = [...capabilities].sort((a, b) => {
    switch (sortBy) {
      case 'proficiency':
        return b.proficiency - a.proficiency;
      case 'usage':
        return b.usageCount - a.usageCount;
      case 'success':
        return b.successRate - a.successRate;
      case 'recent':
        return (b.lastUsed?.getTime() || 0) - (a.lastUsed?.getTime() || 0);
      default:
        return 0;
    }
  });

  const categorizedCapabilities = sortedCapabilities.reduce((acc, capability) => {
    if (!acc[capability.type]) acc[capability.type] = [];
    acc[capability.type].push(capability);
    return acc;
  }, {} as Record<string, AgentCapability[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-content">Agent Capabilities</h4>
        <SelectField
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="proficiency">Sort by Proficiency</option>
          <option value="usage">Sort by Usage</option>
          <option value="success">Sort by Success Rate</option>
          <option value="recent">Sort by Recent Use</option>
        </SelectField>
      </div>

      {Object.entries(categorizedCapabilities).map(([type, typeCapabilities]) => (
        <div key={type}>
          <h5 className="font-medium text-content mb-3 capitalize">
            {type.replace('_', ' ')} Capabilities
          </h5>
          <div className="grid grid-cols-2 gap-4">
            {typeCapabilities.map(capability => (
              <div
                key={capability.id}
                className="border border-line rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                {...clickable(() => onCapabilitySelect?.(capability))}
              >
                <div className="flex items-center justify-between mb-3">
                  <h6 className="font-medium text-content">{capability.name}</h6>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCapabilityProficiencyColor(capability.proficiency)}`}>
                    {capability.proficiency}%
                  </span>
                </div>
                
                <p className="text-sm text-content-secondary mb-3">{capability.description}</p>
                
                <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      capability.proficiency >= 90 ? 'bg-green-500' :
                      capability.proficiency >= 75 ? 'bg-blue-500' :
                      capability.proficiency >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${capability.proficiency}%` }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-content-secondary">Usage:</span>
                    <span className="ml-2 font-medium">{capability.usageCount}</span>
                  </div>
                  <div>
                    <span className="text-content-secondary">Success:</span>
                    <span className="ml-2 font-medium">{(capability.successRate * 100).toFixed(1)}%</span>
                  </div>
                </div>
                
                {capability.lastUsed && (
                  <div className="text-xs text-content-muted mt-2">
                    Last used: {capability.lastUsed.toLocaleDateString()}
                  </div>
                )}
                
                {capability.prerequisites.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-content-secondary mb-1">Prerequisites:</div>
                    <div className="flex flex-wrap gap-1">
                      {capability.prerequisites.map(prereq => (
                        <span key={prereq} className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                          {prereq}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Learned Patterns Tab
const LearnedPatternsTab: React.FC<{
  learningData: AgentLearningData;
}> = ({ learningData }) => {
  const [selectedPattern, setSelectedPattern] = useState<LearnedPattern | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Learned Patterns</h4>
        <p className="text-sm text-content-secondary mb-6">
          Patterns the agent has identified and learned from experience
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {learningData.knowledgeBase.patterns.map(pattern => (
          <div
            key={pattern.id}
            className="border border-line rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            {...clickable(() => setSelectedPattern(pattern))}
          >
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-medium text-content">{pattern.pattern}</h5>
              <span className="text-sm text-content-secondary">
                {(pattern.successRate * 100).toFixed(1)}% success
              </span>
            </div>
            
            <div className="text-sm text-content-secondary mb-3">
              Observed {pattern.frequency} times
            </div>
            
            <div className="flex items-center justify-between text-xs text-content-muted">
              <span>Confidence: {(pattern.confidence * 100).toFixed(1)}%</span>
              <span>First seen: {pattern.firstObserved.toLocaleDateString()}</span>
            </div>
            
            {pattern.context.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-content-secondary mb-1">Context:</div>
                <div className="flex flex-wrap gap-1">
                  {pattern.context.slice(0, 3).map(ctx => (
                    <span key={ctx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                      {ctx}
                    </span>
                  ))}
                  {pattern.context.length > 3 && (
                    <span className="text-xs text-content-muted">+{pattern.context.length - 3} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pattern Detail Modal */}
      {selectedPattern && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-line flex items-center justify-between">
              <h3 className="text-lg font-semibold text-content">Pattern Details</h3>
              <button
                onClick={() => setSelectedPattern(null)}
                className="text-content-subtle hover:text-content-secondary"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-content mb-2">Pattern</h4>
                  <p className="text-gray-700 dark:text-gray-300">{selectedPattern.pattern}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                    <div className="text-sm text-content-secondary">Frequency</div>
                    <div className="font-semibold text-content">{selectedPattern.frequency}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                    <div className="text-sm text-content-secondary">Success Rate</div>
                    <div className="font-semibold text-content">
                      {(selectedPattern.successRate * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                    <div className="text-sm text-content-secondary">Confidence</div>
                    <div className="font-semibold text-content">
                      {(selectedPattern.confidence * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                    <div className="text-sm text-content-secondary">Applications</div>
                    <div className="font-semibold text-content">{selectedPattern.applications.length}</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-content mb-2">Context</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedPattern.context.map(ctx => (
                      <span key={ctx} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                        {ctx}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-content mb-2">Applications</h4>
                  <div className="space-y-2">
                    {selectedPattern.applications.slice(0, 5).map(app => (
                      <div key={app} className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-dark-700 rounded p-2">
                        {app}
                      </div>
                    ))}
                    {selectedPattern.applications.length > 5 && (
                      <div className="text-sm text-content-muted">
                        +{selectedPattern.applications.length - 5} more applications
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm text-content-secondary">
                  <div>
                    <span>First observed:</span>
                    <span className="ml-2 font-medium">{selectedPattern.firstObserved.toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span>Last observed:</span>
                    <span className="ml-2 font-medium">{selectedPattern.lastObserved.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Insights Tab
const InsightsTab: React.FC<{
  learningData: AgentLearningData;
}> = ({ learningData }) => {
  const getInsightIcon = (type: AgentInsight['type']) => {
    const icons = {
      performance: '📊',
      user_behavior: '👥',
      market_trend: '📈',
      optimization_opportunity: '🎯'
    };
    return icons[type];
  };

  const getImpactColor = (impact: 'low' | 'medium' | 'high') => {
    const colors = {
      low: 'text-content-secondary bg-surface-sunken',
      medium: 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
      high: 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
    };
    return colors[impact];
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Generated Insights</h4>
        <p className="text-sm text-content-secondary mb-6">
          Insights discovered by the agent through data analysis and pattern recognition
        </p>
      </div>

      <div className="space-y-4">
        {learningData.knowledgeBase.insights.map(insight => (
          <div key={insight.id} className="border border-line rounded-lg p-4">
            <div className="flex items-start space-x-4">
              <div className="text-2xl">{getInsightIcon(insight.type)}</div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-content">{insight.title}</h5>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(insight.impact)}`}>
                      {insight.impact.toUpperCase()}
                    </span>
                    <span className="text-sm text-content-muted">
                      {Math.round(insight.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-3">{insight.description}</p>
                
                {insight.actionable && insight.recommendations.length > 0 && (
                  <div>
                    <h6 className="font-medium text-content mb-2">Recommendations</h6>
                    <ul className="space-y-1">
                      {insight.recommendations.map((rec, index) => (
                        <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start space-x-2">
                          <span className="text-blue-600 dark:text-blue-300 mt-1">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-3 text-xs text-content-muted">
                  Discovered: {insight.discoveredAt.toLocaleDateString()} • 
                  Type: {insight.type.replace('_', ' ')} • 
                  {insight.actionable ? 'Actionable' : 'Informational'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Evolution Timeline Tab
const EvolutionTimelineTab: React.FC<{
  milestones: LearningMilestone[];
  onMilestoneSelect?: (milestone: LearningMilestone) => void;
  getMilestoneIcon: (type: LearningMilestone['type']) => string;
  getImpactColor: (impact: 'low' | 'medium' | 'high') => string;
}> = ({ milestones, onMilestoneSelect, getMilestoneIcon, getImpactColor }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Learning Evolution Timeline</h4>
        <p className="text-sm text-content-secondary mb-6">
          Chronological view of the agent's learning journey and capability development
        </p>
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>
        
        <div className="space-y-6">
          {milestones.map((milestone, _index) => (
            <div key={milestone.id} className="relative">
              <div
                className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg p-4 -ml-4"
                {...clickable(() => onMilestoneSelect?.(milestone))}
              >
                {/* Timeline dot */}
                <div className="flex-shrink-0 w-12 h-12 bg-surface rounded-full border-4 border-blue-500 flex items-center justify-center text-xl z-10">
                  {getMilestoneIcon(milestone.type)}
                </div>
                
                {/* Milestone content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-content">{milestone.title}</h5>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(milestone.impact)}`}>
                        {milestone.impact.toUpperCase()}
                      </span>
                      <span className="text-sm text-content-muted">
                        {milestone.timestamp.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{milestone.description}</p>
                  
                  {milestone.metrics && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 mb-2">
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Performance Impact</div>
                      <div className="flex items-center space-x-4 text-sm text-blue-800 dark:text-blue-300">
                        <span>Before: {(milestone.metrics.before * 100).toFixed(1)}%</span>
                        <span>→</span>
                        <span>After: {(milestone.metrics.after * 100).toFixed(1)}%</span>
                        <span className="font-medium">
                          (+{(milestone.metrics.improvement * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {milestone.relatedCapabilities.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {milestone.relatedCapabilities.map(capability => (
                        <span key={capability} className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                          {capability.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};